// ticket-files-at-head-commit-check.ts
//
// Security audit M-1 / code review G3 H1: commit-owner SKIPS a pathspec it
// cannot stage (an ignore rule) and still reports success, and the next
// owned-checkout reset deletes an ignored ticket file. So every commit that
// carries ticket files — redirect, recovery, and a done attempt's proposals —
// checks them AT HEAD, byte for byte, before anything is journaled as applied
// or done.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { GitRunner } from "./commit-owner-git-ops.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { loadTicketFilesFromRepo } from "./supervisor-ticket-projection.js";
import { ticketFileName } from "./ticket-file-name.js";

/** The first ticket path whose bytes at HEAD differ from the worktree (or are missing there). */
export async function firstTicketMissingAtHead(
  gitRunner: GitRunner,
  repoDir: string,
  paths: readonly string[],
): Promise<string | undefined> {
  for (const path of paths) {
    const atHead = await gitRunner(["cat-file", "blob", `HEAD:${path}`], repoDir);
    const written = await fs.readFile(join(repoDir, path), "utf8").catch(() => undefined);
    if (atHead.status !== 0 || written === undefined || atHead.stdout !== written) return path;
  }
  return undefined;
}

export const ticketFilePath = (ticket: Pick<Ticket, "id" | "title">): string =>
  join(".avatar", "tickets", ticketFileName(ticket));

/** `ticket_not_committed: …` for the first of `tickets` HEAD lacks, or undefined. */
export async function ticketsNotCommittedReason(
  gitRunner: GitRunner,
  repoDir: string,
  tickets: readonly Pick<Ticket, "id" | "title">[],
): Promise<string | undefined> {
  return notCommittedReason(
    await firstTicketMissingAtHead(gitRunner, repoDir, tickets.map(ticketFilePath)),
  );
}

function notCommittedReason(missing: string | undefined): string | undefined {
  return missing === undefined
    ? undefined
    : `ticket_not_committed: ${missing} không có trong HEAD đúng như đã ghi (bị ignore hoặc không ` +
        "stage được) — file bị ignore sẽ bị dọn khi reset worktree; gỡ luật ignore rồi chạy lại.";
}

/** Done path (review G3 H1): the same check for the tickets a done attempt's proposals touched. */
export async function touchedTicketsNotCommittedReason(
  gitRunner: GitRunner,
  repoDir: string,
  touchedIds: readonly string[],
): Promise<string | undefined> {
  if (touchedIds.length === 0) return undefined;
  // The file actually there: a fixture's or older mission's name may differ
  // from the one the title derives, and a `cancel` leaves the file untouched.
  const paths = (await loadTicketFilesFromRepo(repoDir))
    .filter((entry) => touchedIds.includes(entry.ticket.id))
    .map((entry) => join(".avatar", "tickets", entry.file));
  return notCommittedReason(await firstTicketMissingAtHead(gitRunner, repoDir, paths));
}
